// pregnancyRiskScreeningData.js
// All screen definitions, questions, options and scoring for Pregnancy Risk Screening

export const PREG_STEPS = [
  { id: 'intro',          label: 'Introduction' },
  { id: 'consent',        label: 'Consent' },
  { id: 'confirmation',   label: 'Pregnancy Confirmation' },
  { id: 'details',        label: 'Pregnancy Details' },
  { id: 'medicalHistory', label: 'Medical History' },
  { id: 'symptoms',       label: 'Current Symptoms' },
  { id: 'lifestyle',      label: 'Lifestyle & Habits' },
  { id: 'mentalHealth',   label: 'Mental & Emotional Wellbeing' },
  { id: 'obstetric',      label: 'Obstetric History' },
  { id: 'familyHistory',  label: 'Family History' },
  { id: 'review',         label: 'Review Summary' },
  { id: 'result',         label: 'Risk Result' },
];

export const PREG_FLOW = [
  'intro', 'consent', 'confirmation', 'details',
  'medicalHistory', 'symptoms', 'lifestyle', 'mentalHealth',
  'obstetric', 'familyHistory', 'review', 'result',
];

// ── Intro ──────────────────────────────────────────────────────────────────────
export const PREG_INTRO = {
  badge:        'Pregnancy Risk Screening',
  badgeIcon:    '🤰',
  heading:      'Your pregnancy health check',
  desc:         'A confidential screening to understand your pregnancy risk factors and guide you to the right level of care.',
  bullets: [
    { icon: '⏱️', text: 'Takes less than 8 minutes' },
    { icon: '🔒', text: 'Private & confidential' },
    { icon: '💊', text: 'Clinically informed, not diagnostic' },
  ],
  privacy:      'Your answers are completely private and will never be shared without your consent.',
  startLabel:   'Start screening',
  laterLabel:   "I'll do it later",
  illustrationKey: 'pregnancy',
};

// ── Consent ────────────────────────────────────────────────────────────────────
export const PREG_CONSENT = {
  heading: 'A few things to know',
  paragraphs: [
    'This screening helps identify pregnancy risk factors and guides you to appropriate care. It is not a clinical diagnosis.',
    'If you are experiencing a medical emergency, severe pain, heavy bleeding, or are in immediate distress, please contact emergency services immediately.',
  ],
  checkLabel: 'I understand this screening is for informational purposes only and does not replace advice from a qualified healthcare professional.',
  agreeLabel: 'I understand, continue',
};
export const PREG_CONCERNS = [
  { id: 'highBloodPressure', label: 'Blood pressure',     icon: '🩺' },
  { id: 'gestationalDiabetes', label: 'Gestational diabetes', icon: '🩸' },
  { id: 'bleeding',          label: 'Bleeding or spotting', icon: '🚨' },
  { id: 'swelling',          label: 'Swelling or pain',   icon: '🤕' },
  { id: 'sleepFatigue',      label: 'Sleep & fatigue',    icon: '😴' },
  { id: 'moodAnxiety',       label: 'Mood & anxiety',     icon: '💛' },
  { id: 'fetalMovement',     label: 'Baby movement',      icon: '🤰' },
  { id: 'other',             label: 'Something else',     icon: '···' },
];
// ── Question screens ───────────────────────────────────────────────────────────
export const PREG_QUESTION_SCREENS = [

  // ── Step 2: Pregnancy Confirmation ──────────────────────────────────────────
  {
    id: 'confirmation',
    stepLabel: 'Pregnancy Confirmation',
    title: 'Pregnancy Confirmation',
    subtitle: 'Tell us about your current pregnancy status.',
    icon: '🤰',
    stepNumber: 2,
    questions: [
      {
        id: 'isCurrentlyPregnant',
        label: 'Is this your current pregnancy?',
        type: 'toggle',
        options: [
          { label: 'Yes, I am pregnant',  score: 0 },
          { label: 'No, not pregnant',    score: 0 },
        ],
      },
      {
        id: 'confirmationMethod',
        label: 'How do you know?',
        type: 'toggle',
        options: [
          { label: 'Positive pregnancy test',  score: 0 },
          { label: 'Ultrasound confirmation',  score: 0 },
          { label: 'Clinical confirmation',    score: 0 },
          { label: 'Other',                   score: 0 },
        ],
      },
      {
        id: 'lmpDate',
        label: 'Date of your last menstrual period (LMP)',
        type: 'date',
        placeholder: 'e.g. 12 May 2024',
      },
    ],
  },

  // ── Step 3: Pregnancy Details ────────────────────────────────────────────────
  {
    id: 'details',
    stepLabel: 'Pregnancy Details',
    title: 'Pregnancy Details',
    subtitle: 'Help us understand where you are in your pregnancy journey.',
    icon: '📅',
    stepNumber: 3,
    questions: [
      {
        id: 'gestationalAge',
        label: 'How far along are you?',
        type: 'slider',
        unit: 'weeks',
        min: 0,
        max: 42,
        defaultValue: 10,
        score: (val) => {
          // Higher risk in early (<12) and late (>36) stages
          if (val < 12) return 1;
          if (val > 36) return 2;
          return 0;
        },
      },
      {
        id: 'gravidity',
        label: 'This is my',
        type: 'toggle',
        options: [
          { label: 'First pregnancy',   score: 0 },
          { label: 'Second pregnancy',  score: 0 },
          { label: 'Third or more',     score: 1 },
        ],
      },
      {
        id: 'previousOutcome',
        label: 'Previous pregnancy outcome (if any)',
        type: 'toggle',
        placeholder: 'Select an option',
        options: [
          { label: 'N/A — first pregnancy',      score: 0 },
          { label: 'Live birth (full term)',      score: 0 },
          { label: 'Live birth (preterm)',        score: 1 },
          { label: 'Miscarriage',                score: 1 },
          { label: 'Stillbirth',                 score: 2 },
          { label: 'Ectopic pregnancy',          score: 2 },
          { label: 'Termination',                score: 0 },
        ],
      },
    ],
  },

  // ── Step 4: Medical History ──────────────────────────────────────────────────
  {
    id: 'medicalHistory',
    stepLabel: 'Medical History',
    title: 'Medical History',
    subtitle: 'Do you have any of the following medical conditions?',
    icon: '🏥',
    stepNumber: 4,
    questions: [
      {
        id: 'highBloodPressure',
        label: 'High blood pressure',
        type: 'yesNo',
        redFlag: true,
        options: [
          { label: 'No',  score: 0 },
          { label: 'Yes', score: 3 },
        ],
      },
      {
        id: 'diabetes',
        label: 'Diabetes (Type 1 or 2)',
        type: 'yesNo',
        redFlag: true,
        options: [
          { label: 'No',  score: 0 },
          { label: 'Yes', score: 3 },
        ],
      },
      {
        id: 'thyroidDisorder',
        label: 'Thyroid disorder',
        type: 'yesNo',
        options: [
          { label: 'No',  score: 0 },
          { label: 'Yes', score: 2 },
        ],
      },
      {
        id: 'asthma',
        label: 'Asthma',
        type: 'yesNo',
        options: [
          { label: 'No',  score: 0 },
          { label: 'Yes', score: 1 },
        ],
      },
      {
        id: 'heartDisease',
        label: 'Heart disease',
        type: 'yesNo',
        redFlag: true,
        options: [
          { label: 'No',  score: 0 },
          { label: 'Yes', score: 3 },
        ],
      },
      {
        id: 'kidneyDisease',
        label: 'Kidney disease',
        type: 'yesNo',
        options: [
          { label: 'No',  score: 0 },
          { label: 'Yes', score: 2 },
        ],
      },
      {
        id: 'autoimmuneDisorder',
        label: 'Autoimmune disorder',
        type: 'yesNo',
        options: [
          { label: 'No',  score: 0 },
          { label: 'Yes', score: 2 },
        ],
      },
    ],
    expandable: {
      triggerLabel: '+ View more conditions',
      extraQuestions: [
        {
          id: 'epilepsy',
          label: 'Epilepsy / seizure disorder',
          type: 'yesNo',
          options: [
            { label: 'No',  score: 0 },
            { label: 'Yes', score: 2 },
          ],
        },
        {
          id: 'bloodClottingDisorder',
          label: 'Blood clotting disorder',
          type: 'yesNo',
          options: [
            { label: 'No',  score: 0 },
            { label: 'Yes', score: 3 },
          ],
        },
        {
          id: 'mentalHealthCondition',
          label: 'Mental health condition (diagnosed)',
          type: 'yesNo',
          options: [
            { label: 'No',  score: 0 },
            { label: 'Yes', score: 1 },
          ],
        },
      ],
    },
  },

  // ── Step 5: Current Symptoms ─────────────────────────────────────────────────
  {
    id: 'symptoms',
    stepLabel: 'Current Symptoms',
    title: 'Current Symptoms',
    subtitle: 'Are you experiencing any of the following symptoms?',
    icon: '🩺',
    stepNumber: 5,
    questions: [
      {
        id: 'vaginalBleeding',
        label: 'Vaginal bleeding',
        type: 'yesNo',
        redFlag: true,
        infoTip: 'Any bleeding during pregnancy should be reported to your doctor promptly.',
        options: [
          { label: 'No',  score: 0 },
          { label: 'Yes', score: 4 },
        ],
      },
      {
        id: 'severeAbdominalPain',
        label: 'Severe abdominal pain',
        type: 'yesNo',
        redFlag: true,
        infoTip: 'Severe or persistent abdominal pain can indicate a serious complication.',
        options: [
          { label: 'No',  score: 0 },
          { label: 'Yes', score: 4 },
        ],
      },
      {
        id: 'severeHeadache',
        label: 'Severe headache',
        type: 'yesNo',
        redFlag: true,
        infoTip: 'Severe headaches may be a sign of pre-eclampsia.',
        options: [
          { label: 'No',  score: 0 },
          { label: 'Yes', score: 3 },
        ],
      },
      {
        id: 'blurredVision',
        label: 'Blurred vision',
        type: 'yesNo',
        redFlag: true,
        infoTip: 'Visual disturbances can be a warning sign of pre-eclampsia.',
        options: [
          { label: 'No',  score: 0 },
          { label: 'Yes', score: 3 },
        ],
      },
      {
        id: 'swellingFaceHands',
        label: 'Swelling in face / hands',
        type: 'yesNo',
        redFlag: true,
        infoTip: 'Sudden swelling in the face or hands may indicate pre-eclampsia.',
        options: [
          { label: 'No',  score: 0 },
          { label: 'Yes', score: 3 },
        ],
      },
      {
        id: 'fever',
        label: 'Fever',
        type: 'yesNo',
        infoTip: 'Fever during pregnancy should be evaluated by a doctor.',
        options: [
          { label: 'No',  score: 0 },
          { label: 'Yes', score: 2 },
        ],
      },
      {
        id: 'shortnessOfBreath',
        label: 'Shortness of breath',
        type: 'yesNo',
        infoTip: 'Difficulty breathing beyond normal pregnancy changes should be assessed.',
        options: [
          { label: 'No',  score: 0 },
          { label: 'Yes', score: 2 },
        ],
      },
    ],
  },

  // ── Step 6: Lifestyle & Habits ───────────────────────────────────────────────
  {
    id: 'lifestyle',
    stepLabel: 'Lifestyle & Habits',
    title: 'Lifestyle & Habits',
    subtitle: 'Tell us about your lifestyle to help us understand your overall risk.',
    icon: '🌿',
    stepNumber: 6,
    questions: [
      {
        id: 'smoking',
        label: 'Do you smoke?',
        type: 'toggle',
        options: [
          { label: 'No',         score: 0 },
          { label: 'Yes',        score: 3 },
          { label: 'Sometimes',  score: 2 },
        ],
      },
      {
        id: 'alcohol',
        label: 'Do you consume alcohol?',
        type: 'toggle',
        options: [
          { label: 'No',         score: 0 },
          { label: 'Yes',        score: 3 },
          { label: 'Sometimes',  score: 2 },
        ],
      },
      {
        id: 'physicalActivity',
        label: 'How would you describe your physical activity?',
        type: 'toggle',
        placeholder: 'Select an option',
        options: [
          { label: 'Sedentary (little to no exercise)',  score: 2 },
          { label: 'Light (1–2 days/week)',              score: 1 },
          { label: 'Moderate (3–4 days/week)',           score: 0 },
          { label: 'Active (5+ days/week)',              score: 0 },
        ],
      },
      {
        id: 'sleepQuality',
        label: 'How is your sleep quality?',
        type: 'toggle',
        placeholder: 'Select an option',
        options: [
          { label: 'Very good',  score: 0 },
          { label: 'Good',       score: 0 },
          { label: 'Fair',       score: 1 },
          { label: 'Poor',       score: 2 },
          { label: 'Very poor',  score: 3 },
        ],
      },
    ],
  },

  // ── Step 7: Mental & Emotional Wellbeing ─────────────────────────────────────
  {
    id: 'mentalHealth',
    stepLabel: 'Mental & Emotional Wellbeing',
    title: 'Mental & Emotional Wellbeing',
    subtitle: 'How have you been feeling over the past 2 weeks? Rate the following.',
    icon: '💛',
    stepNumber: 7,
    questions: [
      {
        id: 'anxietyLevel',
        label: 'Anxiety or worry',
        type: 'slider',
        min: 0,
        max: 10,
        minLabel: 'None',
        maxLabel: 'Severe',
        defaultValue: 0,
        score: (val) => {
          if (val <= 2) return 0;
          if (val <= 5) return 1;
          if (val <= 7) return 2;
          return 3;
        },
      },
      {
        id: 'lowMoodLevel',
        label: 'Feeling low or sad',
        type: 'slider',
        min: 0,
        max: 10,
        minLabel: 'None',
        maxLabel: 'Severe',
        defaultValue: 0,
        score: (val) => {
          if (val <= 2) return 0;
          if (val <= 5) return 1;
          if (val <= 7) return 2;
          return 3;
        },
      },
      {
        id: 'irritabilityLevel',
        label: 'Irritability / mood swings',
        type: 'slider',
        min: 0,
        max: 10,
        minLabel: 'None',
        maxLabel: 'Severe',
        defaultValue: 0,
        score: (val) => {
          if (val <= 2) return 0;
          if (val <= 5) return 1;
          if (val <= 7) return 2;
          return 3;
        },
      },
      {
        id: 'overwhelmedLevel',
        label: 'Feeling overwhelmed',
        type: 'slider',
        min: 0,
        max: 10,
        minLabel: 'None',
        maxLabel: 'Severe',
        defaultValue: 0,
        score: (val) => {
          if (val <= 2) return 0;
          if (val <= 5) return 1;
          if (val <= 7) return 2;
          return 3;
        },
      },
      {
        id: 'wantsTalkToSomeone',
        label: 'Would you like to talk to someone about how you\'re feeling?',
        type: 'toggle',
        options: [
          { label: 'No',  score: 0 },
          { label: 'Yes', score: 1 },
        ],
      },
    ],
  },

  // ── Step 8: Obstetric History ────────────────────────────────────────────────
  {
    id: 'obstetric',
    stepLabel: 'Obstetric History',
    title: 'Obstetric History',
    subtitle: 'Have you had any of the following in previous pregnancies?',
    icon: '📋',
    stepNumber: 8,
    questions: [
      {
        id: 'prevMiscarriage',
        label: 'Miscarriage',
        type: 'yesNo',
        options: [
          { label: 'No',  score: 0 },
          { label: 'Yes', score: 2 },
        ],
      },
      {
        id: 'prevPretermDelivery',
        label: 'Preterm delivery',
        type: 'yesNo',
        options: [
          { label: 'No',  score: 0 },
          { label: 'Yes', score: 2 },
        ],
      },
      {
        id: 'prevHighBloodPressure',
        label: 'High blood pressure (during pregnancy)',
        type: 'yesNo',
        redFlag: true,
        options: [
          { label: 'No',  score: 0 },
          { label: 'Yes', score: 3 },
        ],
      },
      {
        id: 'prevGestationalDiabetes',
        label: 'Gestational diabetes',
        type: 'yesNo',
        options: [
          { label: 'No',  score: 0 },
          { label: 'Yes', score: 2 },
        ],
      },
      {
        id: 'prevCesarean',
        label: 'Cesarean delivery',
        type: 'yesNo',
        options: [
          { label: 'No',  score: 0 },
          { label: 'Yes', score: 1 },
        ],
      },
      {
        id: 'prevLowBirthWeight',
        label: 'Birth of low birth weight baby',
        type: 'yesNo',
        options: [
          { label: 'No',  score: 0 },
          { label: 'Yes', score: 2 },
        ],
      },
      {
        id: 'deliveryComplication',
        label: 'Any complications during delivery?',
        type: 'toggle',
        placeholder: 'Select an option',
        options: [
          { label: 'None',                        score: 0 },
          { label: 'Postpartum haemorrhage',      score: 2 },
          { label: 'Placental abruption',         score: 3 },
          { label: 'Placenta previa',             score: 3 },
          { label: 'Cord complications',          score: 2 },
          { label: 'Prolonged labour',            score: 1 },
          { label: 'Emergency C-section',         score: 2 },
          { label: 'Other',                       score: 1 },
        ],
      },
    ],
  },

  // ── Step 9: Family History ────────────────────────────────────────────────────
  {
    id: 'familyHistory',
    stepLabel: 'Family History',
    title: 'Family History',
    subtitle: 'Do any of your close family members have the following conditions?',
    icon: '👨‍👩‍👧',
    stepNumber: 9,
    questions: [
      {
        id: 'familyDiabetes',
        label: 'Diabetes',
        type: 'yesNo',
        options: [
          { label: 'No',  score: 0 },
          { label: 'Yes', score: 1 },
        ],
      },
      {
        id: 'familyHighBloodPressure',
        label: 'High blood pressure',
        type: 'yesNo',
        options: [
          { label: 'No',  score: 0 },
          { label: 'Yes', score: 1 },
        ],
      },
      {
        id: 'familyHeartDisease',
        label: 'Heart disease',
        type: 'yesNo',
        options: [
          { label: 'No',  score: 0 },
          { label: 'Yes', score: 1 },
        ],
      },
      {
        id: 'familyThyroidDisorder',
        label: 'Thyroid disorder',
        type: 'yesNo',
        options: [
          { label: 'No',  score: 0 },
          { label: 'Yes', score: 1 },
        ],
      },
      {
        id: 'familyBloodClottingDisorder',
        label: 'Blood clotting disorder',
        type: 'yesNo',
        options: [
          { label: 'No',  score: 0 },
          { label: 'Yes', score: 2 },
        ],
      },
      {
        id: 'familyGeneticDisorders',
        label: 'Genetic disorders',
        type: 'yesNo',
        options: [
          { label: 'No',  score: 0 },
          { label: 'Yes', score: 2 },
        ],
      },
    ],
  },
];

// ── Review sections ────────────────────────────────────────────────────────────
export const PREG_REVIEW_SECTIONS = [
  {
    id: 'confirmation',
    icon: '🤰',
    label: 'Pregnancy Confirmation',
    fields: ['isCurrentlyPregnant', 'confirmationMethod', 'lmpDate'],
  },
  {
    id: 'details',
    icon: '📅',
    label: 'Pregnancy Details',
    fields: ['gestationalAge', 'gravidity', 'previousOutcome'],
  },
  {
    id: 'medicalHistory',
    icon: '🏥',
    label: 'Medical History',
    fields: ['highBloodPressure', 'diabetes', 'thyroidDisorder', 'asthma', 'heartDisease', 'kidneyDisease', 'autoimmuneDisorder'],
  },
  {
    id: 'symptoms',
    icon: '🩺',
    label: 'Current Symptoms',
    fields: ['vaginalBleeding', 'severeAbdominalPain', 'severeHeadache', 'blurredVision', 'swellingFaceHands', 'fever', 'shortnessOfBreath'],
  },
  {
    id: 'lifestyle',
    icon: '🌿',
    label: 'Lifestyle & Habits',
    fields: ['smoking', 'alcohol', 'physicalActivity', 'sleepQuality'],
  },
  {
    id: 'mentalHealth',
    icon: '💛',
    label: 'Mental & Emotional Wellbeing',
    fields: ['anxietyLevel', 'lowMoodLevel', 'irritabilityLevel', 'overwhelmedLevel', 'wantsTalkToSomeone'],
  },
  {
    id: 'obstetric',
    icon: '📋',
    label: 'Obstetric History',
    fields: ['prevMiscarriage', 'prevPretermDelivery', 'prevHighBloodPressure', 'prevGestationalDiabetes', 'prevCesarean', 'prevLowBirthWeight', 'deliveryComplication'],
  },
  {
    id: 'familyHistory',
    icon: '👨‍👩‍👧',
    label: 'Family History',
    fields: ['familyDiabetes', 'familyHighBloodPressure', 'familyHeartDisease', 'familyThyroidDisorder', 'familyBloodClottingDisorder', 'familyGeneticDisorders'],
  },
];

// ── Scoring ────────────────────────────────────────────────────────────────────
// Weights reflect clinical significance of each domain
const WEIGHTS = {
  confirmation:   0.0,  // informational only, no risk weight
  details:        1.0,
  medicalHistory: 2.0,  // strongest predictor of maternal risk
  symptoms:       2.5,  // acute symptoms carry highest immediate weight
  lifestyle:      1.2,
  mentalHealth:   1.0,
  obstetric:      1.5,
  familyHistory:  0.8,
};

// Maximum possible raw score per domain (sum of highest option scores)
const MAX_RAW = {
  confirmation:   0,
  details:        3,    // gestational age(2) + gravidity(1)
  medicalHistory: 21,   // 7 conditions × max 3 each
  symptoms:       25,   // 7 symptoms — bleeding/pain/headache are 4 pts each
  lifestyle:      8,    // 4 questions
  mentalHealth:   12,   // 4 sliders × max 3 + wantsTalk(1)
  obstetric:      15,
  familyHistory:  10,
};

// Red-flag symptoms that trigger immediate escalation regardless of total score
const RED_FLAG_FIELDS = [
  'vaginalBleeding',
  'severeAbdominalPain',
  'severeHeadache',
  'blurredVision',
  'swellingFaceHands',
];

export function computePregScore(answers) {
  // Check for immediate red-flag symptoms
  const hasRedFlag = RED_FLAG_FIELDS.some(
    (field) => answers[field] === 'Yes'
  );
  if (hasRedFlag) {
    return { pct: 100, redFlag: true, crisis: false };
  }

  // Check for high-risk medical conditions combination
  const highRiskConditions = ['highBloodPressure', 'diabetes', 'heartDisease'];
  const activeHighRisk = highRiskConditions.filter((f) => answers[f] === 'Yes');
  if (activeHighRisk.length >= 2) {
    return { pct: 90, redFlag: false, crisis: false };
  }

  let weighted = 0;
  let maxWeighted = 0;

  PREG_QUESTION_SCREENS.forEach((screen) => {
    const w      = WEIGHTS[screen.id] ?? 1;
    const maxRaw = MAX_RAW[screen.id]  ?? 10;
    let raw = 0;

    screen.questions.forEach((q) => {
      const val = answers[q.id];
      if (val === undefined || val === null) return;

      if (q.type === 'slider' && typeof q.score === 'function') {
        raw += q.score(val);
      } else if (q.type === 'toggle' || q.type === 'toggle') {
        const opt = q.options?.find((o) => o.label === val);
        if (opt) raw += opt.score ?? 0;
      } else if (q.type === 'yesNo' || q.type === 'toggle') {
        const opt = q.options?.find((o) => o.label === val);
        if (opt) raw += opt.score ?? 0;
      }

      // Include expandable extra questions
      if (screen.expandable?.extraQuestions) {
        screen.expandable.extraQuestions.forEach((eq) => {
          const eVal = answers[eq.id];
          if (!eVal) return;
          const eOpt = eq.options?.find((o) => o.label === eVal);
          if (eOpt) raw += eOpt.score ?? 0;
        });
      }
    });

    if (w > 0) {
      weighted    += raw * w;
      maxWeighted += maxRaw * w;
    }
  });

  const pct = maxWeighted > 0 ? Math.round((weighted / maxWeighted) * 100) : 0;
  return { pct, redFlag: false, crisis: false };
}

export function getPregRisk(pct, redFlag = false) {
  if (redFlag) return {
    level: 'Seek Immediate Care',
    emoji: '🆘',
    color: '#dc2626',
    bg: '#fef2f2',
    border: '#fecaca',
    desc: 'You have reported one or more symptoms that require urgent medical attention. Please contact your doctor or go to the nearest emergency department immediately.',
    means: 'Symptoms such as vaginal bleeding, severe abdominal pain, severe headache, visual disturbances, or sudden swelling can indicate serious pregnancy complications that need prompt evaluation.',
    crisis: true,
    actions: [
      { icon: '🏥', title: 'Go to emergency department now',          desc: 'Do not wait — these symptoms need immediate evaluation.' },
      { icon: '📞', title: 'Call your OB/GYN or midwife',            desc: 'Contact your healthcare provider without delay.' },
      { icon: '🚑', title: 'Call emergency services (112 / 108)',     desc: 'If you cannot travel safely, call for an ambulance.' },
    ],
  };

  if (pct < 20) return {
    level: 'Low Risk',
    emoji: '✅',
    color: '#16a34a',
    bg: '#f0fdf4',
    border: '#bbf7d0',
    desc: 'Your responses suggest a low-risk pregnancy. Continue with your regular antenatal care schedule.',
    means: 'No significant risk factors were identified. Maintaining a healthy lifestyle and attending routine check-ups will support a healthy pregnancy.',
    crisis: false,
    actions: [
      { icon: '📅', title: 'Attend routine antenatal visits',         desc: 'Keep up with your scheduled check-ups and scans.' },
      { icon: '🥗', title: 'Maintain a healthy diet & lifestyle',     desc: 'Nutrition, gentle activity and rest all matter.' },
      { icon: '📊', title: 'Track your pregnancy health',            desc: 'Use our pregnancy tracker to monitor key milestones.' },
    ],
  };

  if (pct < 50) return {
    level: 'Moderate Risk',
    emoji: '💛',
    color: '#d97706',
    bg: '#fffbeb',
    border: '#fde68a',
    desc: 'Some risk factors have been identified. More frequent monitoring and specialist review may be advised.',
    means: 'Your responses indicate some areas that warrant closer attention. Your healthcare provider may recommend additional tests or more frequent visits.',
    crisis: false,
    actions: [
      { icon: '👩‍⚕️', title: 'Discuss findings with your OB/GYN',   desc: 'Share this report with your doctor at your next visit.' },
      { icon: '🔬', title: 'Additional screening may be needed',      desc: 'Your provider may recommend specialist tests or scans.' },
      { icon: '💬', title: 'Mental wellness check-in',               desc: 'Emotional wellbeing is part of a healthy pregnancy.' },
    ],
  };

  return {
    level: 'High Risk',
    emoji: '🔴',
    color: '#dc2626',
    bg: '#fef2f2',
    border: '#fecaca',
    desc: 'Multiple significant risk factors have been identified. Please consult a specialist as soon as possible.',
    means: 'Your responses indicate a high-risk pregnancy profile. Close monitoring by an obstetric specialist is strongly recommended to ensure the best outcomes for you and your baby.',
    crisis: false,
    actions: [
      { icon: '👩‍⚕️', title: 'See a maternal-fetal medicine specialist', desc: 'Request a referral to a high-risk pregnancy specialist.' },
      { icon: '📞', title: 'iMumz Helpline: 1800-XXX-XXXX',           desc: 'Free, confidential pregnancy support helpline.' },
      { icon: '🏥', title: 'Consider a higher-level facility',         desc: 'A hospital with a NICU may be recommended for delivery.' },
    ],
  };
}